import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage PWA installation
 * Handles beforeinstallprompt for Chrome/Android and detects iOS
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed as standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // For iOS, we can show install instructions if not installed
    if (isIOSDevice && !isStandalone) {
      setIsInstallable(true);
    }

    // Handle beforeinstallprompt for Chrome/Edge/Android
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  /**
   * Trigger the install prompt (Chrome/Android only)
   */
  const promptInstall = useCallback(async () => {
    if (isIOS) {
      // For iOS, show manual instructions
      setShowIOSInstructions(true);
      return { outcome: 'ios-instructions-shown' };
    }

    if (!deferredPrompt) {
      return { outcome: 'no-prompt-available' };
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    // Clear the deferred prompt - it can only be used once
    setDeferredPrompt(null);
    
    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    
    return { outcome };
  }, [deferredPrompt, isIOS]);

  /**
   * Dismiss iOS instructions modal
   */
  const dismissIOSInstructions = useCallback(() => {
    setShowIOSInstructions(false);
  }, []);

  /**
   * Dismiss the install banner (user chose not to install now)
   */
  const dismissInstallBanner = useCallback(() => {
    // Store in localStorage to remember user's choice
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setIsInstallable(false);
  }, []);

  /**
   * Check if banner was recently dismissed (within 7 days)
   */
  const wasBannerDismissed = useCallback(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (!dismissed) return false;
    
    const dismissedTime = parseInt(dismissed, 10);
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return dismissedTime > sevenDaysAgo;
  }, []);

  return {
    isInstallable: isInstallable && !wasBannerDismissed(),
    isInstalled,
    isIOS,
    showIOSInstructions,
    promptInstall,
    dismissIOSInstructions,
    dismissInstallBanner,
  };
}
